import type { DailySchedulePlan } from './types';

export type DuplicateAssignmentKind = 'planner' | 'published_split';

export interface DuplicateAssignmentDetail {
  jobId: string;
  kind: DuplicateAssignmentKind;
  title: string;
  vehicles: Array<{
    vehicleId: string;
    fleetNumber: string;
    allFixed: boolean;
    legSummaries: string[];
  }>;
}

/**
 * Enrich duplicate job_id findings for operator UX.
 * - published_split: already-published trips for one job sit on 2+ buses (cannot unassign here)
 * - planner: draft plan assigned the same job onto multiple vehicles (must consolidate)
 */
export function enrichDuplicateAssignments(plan: DailySchedulePlan): DuplicateAssignmentDetail[] {
  const jobsById = new Map((plan.jobs ?? []).map((j) => [j.id, j]));
  const titles = plan.jobTitles ?? {};

  return plan.duplicateJobs.map((dup) => {
    const vehicles = dup.vehicleIds.map((vehicleId) => {
      const chain = plan.vehicles.find((v) => v.vehicleId === vehicleId);
      const legs = (chain?.legs ?? []).filter((l) => l.jobId === dup.jobId);
      return {
        vehicleId,
        fleetNumber: chain?.fleetNumber ?? vehicleId.slice(0, 8),
        allFixed: legs.length > 0 && legs.every((l) => Boolean(l.isFixed)),
        legSummaries: legs.map((l) => l.label ?? l.legId),
      };
    });

    const kind: DuplicateAssignmentKind =
      vehicles.length > 0 && vehicles.every((v) => v.allFixed) ? 'published_split' : 'planner';

    return {
      jobId: dup.jobId,
      kind,
      title: titles[dup.jobId] ?? jobsById.get(dup.jobId)?.title ?? `Job ${dup.jobId.slice(0, 8)}`,
      vehicles,
    };
  });
}

export function actionableDuplicateJobs(plan: DailySchedulePlan): DuplicateAssignmentDetail[] {
  return enrichDuplicateAssignments(plan).filter((d) => d.kind === 'planner');
}
